import { useState } from 'react';
import { Copy, Check } from 'lucide-react';

/**
 * A copy-pastable login-credentials block: Group / Username / Password.
 *
 * Shown once when the BestAcco team generates a login (invite / reset). The
 * "Copy" button copies all three fields as a clean labelled block so staff can
 * paste them straight into WhatsApp/email without retyping. Rendered monospace
 * for unambiguous characters (0/O, 1/l).
 */
export function CredentialsBlock({
  group,
  username,
  password,
}: {
  group: string | null | undefined;
  username: string | null | undefined;
  password: string;
}) {
  const [copied, setCopied] = useState(false);

  // Only include the rows we actually have; password is always present.
  const rows: Array<[string, string]> = [];
  if (group) rows.push(['Group', group]);
  if (username) rows.push(['Username', username]);
  rows.push(['Password', password]);

  const asText = rows.map(([k, v]) => `${k}: ${v}`).join('\n');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(asText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the block is still selectable by hand */
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-200 bg-white">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          Login credentials
        </span>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          aria-label="Copy all credentials"
        >
          {copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="p-3">
        <pre className="whitespace-pre-wrap break-all font-mono text-[13px] leading-relaxed text-gray-900 select-all">
{rows.map(([k, v]) => `${k.padEnd(9)}: ${v}`).join('\n')}
        </pre>
      </div>
    </div>
  );
}
